/**
 * One row of the Lines panel (W2-07; DESIGN.md §4.10): eye / solo / lock toggles (preview and editing aids only),
 * export index, name (tree indent, expand arrow, type icon, editor tag colour, inline rename, badges for "also turns
 * with parent", pinned lane, also shown, focused hitsounds), type, notes count, "Now" (animating now; a ghost icon
 * when Phira draws nothing for the line at the playhead, with the reason), draw order and group.
 * Colour is never the only signal: every state has an icon and an accessible name.
 */

import { memo, useEffect, useRef, type DragEvent, type MouseEvent, type ReactNode } from 'react';
import type { LineId } from '../../core/model/types';
import { useT } from '../../i18n/useT';
import { Icon } from '../../ui/Icon';
import { IconButton } from '../../ui/IconButton';
import { Swatch } from '../../ui/Swatch';
import { Tooltip } from '../../ui/Tooltip';
import { cx } from '../../ui/internal/cx';
import type { FilterSet } from './lineActions';
import { m } from './lines.messages';
import { TYPE_ICONS, UI_ELEMENT_ICONS, type LineRowModel } from './lineModel';
import type { HiddenReason } from './nowState';
import s from './Lines.module.css';

export type DropZone = 'before' | 'after' | 'onto';

export interface RowFlags {
  readonly selected: boolean;
  readonly active: boolean;
  readonly cursor: boolean;
  readonly hidden: boolean;
  readonly solo: boolean;
  /** Solo is on for other lines: this one is not drawn in the preview. */
  readonly soloedOut: boolean;
  readonly locked: boolean;
  readonly focusHitsound: boolean;
  readonly pinned: boolean;
  readonly alsoShown: boolean;
}

export interface RowNow {
  readonly animating: boolean;
  readonly hidden: HiddenReason | null;
}

export interface LineRowProps {
  readonly row: LineRowModel;
  readonly domId: string;
  readonly top: number;
  readonly height: number;
  readonly flags: RowFlags;
  /** Null while the "now" facts are unknown (no engine). */
  readonly now: RowNow | null;
  readonly renaming: boolean;
  readonly drop: { readonly zone: DropZone; readonly refused: boolean } | null;
  readonly draggable: boolean;
  readonly onPointerSelect: (id: LineId, e: MouseEvent) => void;
  readonly onToggle: (id: LineId, set: FilterSet) => void;
  readonly onExpand: (id: LineId) => void;
  readonly onRename: (id: LineId) => void;
  readonly onRenameDone: (id: LineId, name: string | null) => void;
  readonly onUnpin: (id: LineId) => void;
  readonly onStopAlsoShow: (id: LineId) => void;
  readonly onDragStart: (id: LineId, e: DragEvent<HTMLDivElement>) => void;
  readonly onDragOver: (id: LineId, e: DragEvent<HTMLDivElement>) => void;
  readonly onDrop: (id: LineId, e: DragEvent<HTMLDivElement>) => void;
  readonly onDragEnd: () => void;
  readonly onContextMenu: (id: LineId) => void;
}

function RenameInput(props: { readonly initial: string; readonly label: string; onDone(name: string | null): void }) {
  const ref = useRef<HTMLInputElement>(null);
  const done = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (el === null) return;
    el.focus({ preventScroll: true });
    el.select();
  }, []);
  const finish = (name: string | null): void => {
    if (done.current) return;
    done.current = true;
    props.onDone(name);
  };
  return (
    <input
      ref={ref}
      className={s.renameInput}
      defaultValue={props.initial}
      aria-label={props.label}
      maxLength={200}
      spellCheck={false}
      autoComplete="off"
      onClick={(e) => {
        e.stopPropagation();
      }}
      onDoubleClick={(e) => {
        e.stopPropagation();
      }}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
          e.preventDefault();
          finish(e.currentTarget.value);
        } else if (e.key === 'Escape') {
          e.preventDefault();
          finish(null);
        }
      }}
      onBlur={(e) => {
        finish(e.currentTarget.value);
      }}
    />
  );
}

function LineRowImpl(props: LineRowProps) {
  const t = useT();
  const { row, flags, now } = props;
  const info = row.info;
  const id = info.id;
  const toggle = (set: FilterSet) => (): void => {
    props.onToggle(id, set);
  };
  const stop = (e: MouseEvent): void => {
    e.stopPropagation();
  };
  const typeIcon = info.uiElement !== null ? UI_ELEMENT_ICONS[info.uiElement] : TYPE_ICONS[info.type];
  const typeText =
    info.uiElement !== null ? t(m.typeUi, { element: info.uiElement }) : t(m.typeName, { type: info.type });
  const name = info.name.trim();
  const muted = flags.hidden || flags.soloedOut;

  const badges: ReactNode[] = [];
  if (info.parent !== null && info.rotateWithParent) {
    badges.push(
      <span key="rot" role="img" aria-label={t(m.badgeTurnsWithParent)} title={t(m.badgeTurnsWithParent)}>
        <Icon name="RotateCw" size={12} />
      </span>,
    );
  }
  if (flags.pinned) {
    badges.push(
      <button
        key="pin"
        type="button"
        tabIndex={-1}
        className={s.badgeButton}
        aria-label={t(m.badgeUnpin)}
        title={t(m.badgeUnpin)}
        onClick={(e) => {
          e.stopPropagation();
          props.onUnpin(id);
        }}
      >
        <Icon name="Pin" size={12} />
      </button>,
    );
  }
  if (flags.alsoShown) {
    badges.push(
      <button
        key="also"
        type="button"
        tabIndex={-1}
        className={s.badgeButton}
        aria-label={t(m.badgeStopAlsoShow)}
        title={t(m.badgeStopAlsoShow)}
        onClick={(e) => {
          e.stopPropagation();
          props.onStopAlsoShow(id);
        }}
      >
        <Icon name="Layers" size={12} />
      </button>,
    );
  }
  if (flags.focusHitsound) {
    badges.push(
      <span key="hs" role="img" aria-label={t(m.badgeFocusHitsounds)} title={t(m.badgeFocusHitsounds)}>
        <Icon name="Volume2" size={12} />
      </span>,
    );
  }

  let nowCell: ReactNode = null;
  if (now !== null) {
    if (now.animating) {
      nowCell = (
        <span className={cx(s.now, s.nowAnimating)} role="img" aria-label={t(m.nowAnimating)} title={t(m.nowAnimating)}>
          <Icon name="Activity" size={14} />
        </span>
      );
    } else if (now.hidden !== null) {
      const why = t(m.nowNotDrawn, { reason: now.hidden });
      nowCell = (
        <span className={s.now} role="img" aria-label={why} title={why}>
          <Icon name="Ghost" size={14} />
        </span>
      );
    }
  }

  return (
    <div
      id={props.domId}
      role="row"
      aria-level={row.level + 1}
      aria-selected={flags.selected}
      aria-expanded={row.hasChildren ? row.expanded : undefined}
      data-line={id}
      data-active={flags.active ? 'true' : undefined}
      data-cursor={flags.cursor ? 'true' : undefined}
      data-context={row.context ? 'true' : undefined}
      data-muted={muted ? 'true' : undefined}
      data-drop={props.drop?.zone}
      data-drop-refused={props.drop?.refused === true ? 'true' : undefined}
      className={s.row}
      style={{ top: props.top, height: props.height }}
      draggable={props.draggable && !props.renaming}
      onClick={(e) => {
        props.onPointerSelect(id, e);
      }}
      onDoubleClick={(e) => {
        if ((e.target as Element).closest('button') !== null) return;
        props.onRename(id);
      }}
      onContextMenu={() => {
        props.onContextMenu(id);
      }}
      onDragStart={(e) => {
        props.onDragStart(id, e);
      }}
      onDragOver={(e) => {
        props.onDragOver(id, e);
      }}
      onDrop={(e) => {
        props.onDrop(id, e);
      }}
      onDragEnd={props.onDragEnd}
    >
      <div role="gridcell" className={s.toggles} onDoubleClick={stop}>
        <IconButton
          size="sm"
          tabIndex={-1}
          icon={flags.hidden ? 'EyeOff' : 'Eye'}
          label={{ key: flags.hidden ? m.toggleShow : m.toggleHide }}
          pressed={flags.hidden}
          className={flags.hidden ? undefined : s.toggleOff}
          onClick={(e) => {
            e.stopPropagation();
            toggle('hiddenLines')();
          }}
        />
        <IconButton
          size="sm"
          tabIndex={-1}
          icon="Focus"
          label={{ key: flags.solo ? m.toggleUnsolo : m.toggleSolo }}
          pressed={flags.solo}
          className={flags.solo ? undefined : s.toggleOff}
          onClick={(e) => {
            e.stopPropagation();
            toggle('soloLines')();
          }}
        />
        <IconButton
          size="sm"
          tabIndex={-1}
          icon={flags.locked ? 'Lock' : 'LockOpen'}
          label={{ key: flags.locked ? m.toggleUnlock : m.toggleLock }}
          pressed={flags.locked}
          className={flags.locked ? undefined : s.toggleOff}
          onClick={(e) => {
            e.stopPropagation();
            toggle('lockedLines')();
          }}
        />
      </div>
      <div role="gridcell" className={s.index}>
        {info.index}
      </div>
      <div role="gridcell" className={s.nameCell} style={{ paddingLeft: `${String(row.level * 14)}px` }}>
        {row.hasChildren ? (
          <button
            type="button"
            tabIndex={-1}
            className={s.twisty}
            aria-label={t(row.expanded ? m.collapse : m.expand, {
              name: name === '' ? `#${String(info.index)}` : name,
            })}
            onClick={(e) => {
              e.stopPropagation();
              props.onExpand(id);
            }}
            onDoubleClick={stop}
          >
            <Icon name={row.expanded ? 'ChevronDown' : 'ChevronRight'} size={14} />
          </button>
        ) : (
          <span className={s.twistySpace} />
        )}
        <span className={s.typeIcon} role="img" aria-label={typeText} title={typeText}>
          <Icon name={typeIcon} size={14} />
        </span>
        {info.tag !== null && (
          <span className={s.tagDot} style={{ background: info.tag }} role="img" aria-label={t(m.tagColour)} />
        )}
        {props.renaming ? (
          <RenameInput
            initial={info.name}
            label={t(m.renameLabel, { index: info.index })}
            onDone={(next) => {
              props.onRenameDone(id, next);
            }}
          />
        ) : (
          <Tooltip content={name === '' ? t(m.unnamed) : name} side="top">
            <span className={cx(s.name, name === '' && s.unnamed)}>{name === '' ? t(m.unnamed) : name}</span>
          </Tooltip>
        )}
        {badges.length > 0 && <span className={s.badges}>{badges}</span>}
      </div>
      <div role="gridcell" className={s.typeLabel}>
        {typeText}
      </div>
      <div role="gridcell" className={s.num} aria-label={t(m.notesCount, { count: info.notes })}>
        {info.uiElement !== null && info.notes === 0 ? t(m.notApplicable) : info.notes}
      </div>
      <div role="gridcell" className={s.nowCell}>
        {nowCell}
      </div>
      <div role="gridcell" className={cx(s.num, s.zCell)} aria-label={t(m.zLabel, { z: info.zOrder })}>
        {info.zOrder}
      </div>
      <div role="gridcell" className={s.groupCell}>
        {info.groupColor !== null && <Swatch color={info.groupColor} size={10} />}
        <span className={s.groupName}>{info.groupName ?? t(m.groupDefault)}</span>
      </div>
      {flags.locked && <span className={s.visuallyHidden}>{t(m.lockedBadge)}</span>}
    </div>
  );
}

function shallowSame<T extends object>(a: T | null, b: T | null): boolean {
  if (a === b) return true;
  if (a === null || b === null) return false;
  const ka = Object.keys(a) as (keyof T)[];
  if (ka.length !== Object.keys(b).length) return false;
  return ka.every((k) => Object.is(a[k], b[k]));
}

/** Rows re-render only when something they show changed (flags, now and drop are rebuilt on every tree render). */
export function sameRowProps(a: LineRowProps, b: LineRowProps): boolean {
  for (const k of Object.keys(a) as (keyof LineRowProps)[]) {
    if (k === 'flags' || k === 'now' || k === 'drop') continue;
    if (!Object.is(a[k], b[k])) return false;
  }
  return shallowSame(a.flags, b.flags) && shallowSame(a.now, b.now) && shallowSame(a.drop, b.drop);
}

export const LineRow = memo(LineRowImpl, sameRowProps);
